// ============================================================================
// levelController.js — assemble a battle from a level definition (campaign.js).
// Resets the world, applies difficulty mods, sets bastion ownership, raises the
// enemy warlords / Dynast and the allied generals, places Benjen, seeds the
// opening armies, and builds the objective trackers. Reusable per level (no
// page reload between missions).
// ============================================================================

import { TEAM_MAGMAR, TEAM_PLAYER } from '../core/constants.js';
import { PLAYER_START, WARCAMP } from '../world/battlefield.js';
import { spawnWarlordByName, spawnDynast } from '../ai/warlords.js';
import { spawnGeneral, findGeneral } from './generals.js';
import { spawnInitialArmies } from '../world/spawns.js';
import { buildObjectives } from './objectives.js';
import { resetLevelScript } from './levelScript.js';
import { EV } from '../core/events.js';

const DEFAULT_MODS = { enemyHp: 1, enemyDmg: 1, spawn: 1, magmarNear: 240, allyNear: 135 };

export function loadLevel(world, battlefield, level) {
  world.reset();
  world.level = level;
  world.mods = { ...DEFAULT_MODS, ...(level.mods || {}) };
  world.peaceful = !!level.peaceful;

  // raise this level's arena — geometry, landmarks, scatter, lights, and the four
  // capturable points (populates world.bastions / world.gates for the systems below)
  battlefield.buildArena(world, level);

  // bastion ownership (default: all Magmar-held)
  const magmar = new Set(level.bastionsMagmar || [0, 1, 2, 3]);
  for (const b of world.bastions) {
    b.team = magmar.has(b.id) ? TEAM_MAGMAR : TEAM_PLAYER;
    b.captainSlot = -1; b.respawnT = 0;
    battlefield.recolorBastion(b);
  }
  battlefield.resetGates();

  // enemy officers
  for (const name of (level.warlords || [])) spawnWarlordByName(world, name);
  if (level.dynast) spawnDynast(world);

  // allied generals arrayed near the war-camp; the named leader becomes Warmaster
  const gens = level.allyGenerals || [];
  gens.forEach((name, i) => spawnGeneral(world, name, WARCAMP.x + (i - (gens.length - 1) / 2) * 7, WARCAMP.z - 3));
  if (level.warmaster) world.warmaster = findGeneral(world, level.warmaster) || world.warmaster;

  // place Benjen
  const st = level.start || PLAYER_START;
  const p = world.player;
  p.x = st.x; p.z = st.z; p.px = p.x; p.pz = p.z;
  p.heading = st.heading != null ? st.heading : Math.PI;
  p.hp = p.maxHp; p.titan = 0; p.titanReady = false; p.invuln = 3;

  if (level.morale != null) world.morale = level.morale;

  spawnInitialArmies(world);
  buildObjectives(world, level);
  resetLevelScript(world, level);   // arms the cinematic beats + signals BATTLE_START

  if (world.objectives[0]) {
    world.events.emit(EV.OBJECTIVE, { text: world.objectives.map((o) => o.label).join('  ·  ') });
  }
}
